import React, { useEffect, useState, useCallback } from "react";
import { useCompany } from "../../context/CompanyContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  getLatestTaxComputations,
  getTaxFilings,
  runTaxAction,
} from "../../Services/taxService";
import "./tax.css";

export default function TaxFilings() {
  const { companyId } = useCompany();
  const [filings, setFilings] = useState([]);
  const [computations, setComputations] = useState([]);
  const [selectedComputation, setSelectedComputation] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    try {
      const [filingData, compData] = await Promise.all([
        getTaxFilings(companyId),
        getLatestTaxComputations(companyId),
      ]);

      setFilings(filingData || []);
      setComputations(compData || []);

      setSelectedComputation(
        (prev) => prev || (compData?.length ? compData[0].id : null)
      );
    } catch (err) {
      setMsg(err.message || "Failed to load filings");
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreateFiling() {
    if (!selectedComputation) return;
    setBusy(true);
    setMsg("");
    try {
      await runTaxAction({
        action: "create_filing",
        computationId: selectedComputation,
      });
      setMsg("Tax filing created");
      await load();
    } catch (err) {
      setMsg(err.message || "Failed to create filing");
    } finally {
      setBusy(false);
    }
  }

  const downloadTaxReport = (row) => {
    const doc = new jsPDF();

    const payload = row.filing_payload_json || {};

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Tax Filing Report", 14, 20);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Tax Type: ${row.tax_type_code}`, 14, 30);
    doc.text(`Status: ${row.filing_status}`, 14, 36);
    doc.text(
      `Created: ${new Date(row.created_at).toLocaleDateString()}`,
      14,
      42
    );

    autoTable(doc, {
      startY: 52,
      head: [["Field", "Value"]],
      body: Object.entries(payload).map(([key, value]) => [
        key,
        typeof value === "object" ? JSON.stringify(value) : String(value ?? ""),
      ]),
    });

    doc.save(`${row.tax_type_code}_filing_${row.id.slice(0, 8)}.pdf`);
  };

  return (
    <div className="tax-page">
      <div className="tax-page-header">
        <div>
          <h1>Tax Filings</h1>
          <p>
            Create filing records from approved or validated tax computations.
          </p>
        </div>

        <div className="tax-actions">
          <select
            value={selectedComputation}
            onChange={(e) => setSelectedComputation(e.target.value)}
          >
            <option value="">Select Computation</option>
            {computations.map((c) => (
              <option key={c.id} value={c.id}>
                {c.tax_type_code} - {new Date(c.created_at).toLocaleString()}
              </option>
            ))}
          </select>

          <button
            className="tax-btn primary"
            onClick={handleCreateFiling}
            disabled={busy || !selectedComputation}
          >
            {busy ? "Creating..." : "Create Filing"}
          </button>
        </div>
      </div>

      {msg ? <div className="tax-alert">{msg}</div> : null}

      <div className="tax-panel">
        <h2>Filing Records</h2>

        {filings.length === 0 ? (
          <div className="tax-empty">No tax filings yet.</div>
        ) : (
          <div className="tax-table-wrap">
            <table className="tax-table">
              <thead>
                <tr>
                  <th>Tax Type</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Reference</th>
                  <th>Payload</th>
                </tr>
              </thead>
              <tbody>
                {filings.map((row) => (
                  <tr key={row.id}>
                    <td>{row.tax_type_code}</td>
                    <td>{row.filing_status}</td>
                    <td>{new Date(row.created_at).toLocaleString()}</td>
                    <td>{row.filing_reference || "-"}</td>
                    <td>
                      <pre className="tax-json">
                        {JSON.stringify(row.filing_payload_json || {}, null, 2)}
                      </pre>
                    </td>
                    <td>
                      <button
                        className="btn-primary"
                        onClick={() => downloadTaxReport(row)}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
